import { Inject, Injectable, NgZone } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { determineModeIconUseCase } from '../../determine-mode-icon.use-case';
import { MapCoords, MapLocation } from '../../domain/models/map.models';
import { MapServicesProviderInterface } from '../../domain/ports/secondary/map-services-provider.interface';
import { MapService } from '../../map.service';

import { DroppointListsDetail } from '@api/store-locator/droppoint-lists/models/droppoint-lists.models';
import { PhysicalStoreDetail } from '@api/store-locator/physical-store/models/physical-store.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { LoadLibrary } from '@core/infrastructure/state/core-commands.actions';
import { getCountryCode, getLibraries } from '@core/infrastructure/state/core.selectors';
import { MapPublicUseCases } from '@shared/map';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';

declare global {
    let BMap: any;
    let BMAP_NORMAL_MAP: any;
    let BMapLib: any;
}

@Injectable()
/**
 * Baidu Service
 */
export class BaiduService implements MapServicesProviderInterface {
    private map: any;

    private markers: any;

    private readonly events: string[] = [];

    private readonly POINTS_EVENT = 'points';

    // Si no se le pasa una ciudad a baidu ponemos Pekín por defecto
    private readonly defaultCity = '北京';

    private readonly CLUSTER_EVENT = 'cluster';

    private readonly MAP_EVENT = 'map';

    private markerClusterer: any;

    private currentCoords: any;

    private countryCode: string;

    private readonly baiduClusterMap: string;

    private readonly baiduMapPoints: string;

    private readonly mapEventSubject$: Subject<any> = new Subject();

    public mapEvent$ = this.mapEventSubject$.asObservable();

    constructor(
        private readonly mapService: MapService,
        private readonly store: Store,
        private readonly zone: NgZone,
        private readonly staticResourceService: StaticResourceService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: CorePlatformService
    ) {
        this.baiduClusterMap = this.staticResourceService.getLib('MarkerClusterer');
        this.baiduMapPoints = this.staticResourceService.getLib('TextIconOverlay');

        this.store
            .select(getCountryCode)
            .pipe(first())
            .subscribe((countryCode) => {
                this.countryCode = countryCode;
            });
    }

    public loadMapLibraries(): void {
        if (this.platformService.isBrowser()) {
            const iBaiduMapsKey = this.window.inditex.iBaiduMapsKey;

            this.store.dispatch(
                LoadLibrary({
                    library: 'baiduApi',
                    src: `https://api.map.baidu.com/api?v=2.0&ak=${iBaiduMapsKey}&s=1`,
                })
            );

            this.store.dispatch(
                LoadLibrary({
                    library: 'baiduScripts',
                    src: `https://api.map.baidu.com/getscript?v=2.0&ak=${iBaiduMapsKey}&services=`,
                })
            );

            this.store
                .select(getLibraries)
                .pipe(
                    filter((libs) => libs?.baiduApi?.loaded && libs?.baiduScripts?.loaded && !libs?.baiduMapPoints?.loaded),
                    first()
                ).subscribe(() => {
                    this.store.dispatch(
                        LoadLibrary({
                            library: 'baiduMapPoints',
                            src: 'https://api.map.baidu.com/library/TextIconOverlay/1.2/src/TextIconOverlay_min.js',
                        })
                    );
                });

            this.store
                .select(getLibraries)
                .pipe(
                    filter((libs) => libs?.baiduApi?.loaded
                && libs?.baiduScripts?.loaded
                && libs?.baiduMapPoints?.loaded
                && !libs?.baiduClusterMap?.loaded),
                    first()
                ).subscribe(() => {
                    this.store.dispatch(
                        LoadLibrary({
                            library: 'baiduClusterMap',
                            src: 'https://api.map.baidu.com/library/MarkerClusterer/1.2/src/MarkerClusterer_min.js',
                        })
                    );
                });
        }
    }

    public setCenter(coords: MapCoords): void {
        const bCoords = new BMap.Point(coords.lng, coords.lat);

        if (bCoords && this.map && !this.map?.getCenter().equals(bCoords)) {
            this.map.setCenter(bCoords);
            this.currentCoords = this.map.getCenter();
        }

        if (coords.zoom) {
            this.setZoom(coords.zoom);
        }
    }

    public setZoom(zoom: number): void {
        if (this.map) {
            this.map.setZoom(zoom);
        }
    }

    private setMapEvent(event: { kind: string; data: any }): void {
        this.mapEventSubject$.next(event);
    }

    public initMap(coords: MapCoords, container: HTMLElement): void {
        setTimeout(()=> {
            this.map = new BMap.Map(container?.id ?? 'map');

            if (this.map) {
                this.currentCoords = this.map.getCenter();
                this.map.centerAndZoom(new BMap.Point(coords.lng, coords.lat), 5);

                this.loadControls();
                this.setListeners();
                // this.setMarkers();
                this.setMapEvent({ kind: 'idle', data: {} });
            }
        }, 500);
    }

    private loadControls(): void {
        this.map.enableScrollWheelZoom();
    }

    private setListeners(): void {
        this.map.addEventListener('dragend', () => {
            this.zone.run(() => {
                this.setMapEvent({ kind: 'dragend', data: {} });
            });
        });
    }

    public lookIfBoundsChanged(): boolean {
        const mapCenter = this.map.getCenter();

        const p1 = {
            latitude: Number(mapCenter.lat),
            longitude: Number(mapCenter.lng),
        };

        const p2 = {
            latitude: Number(this.currentCoords.lat),
            longitude: Number(this.currentCoords.lng),
        };

        const distance = Number((MapPublicUseCases.calcDistanceBetweenUseCase(p1, p2) / 1000).toFixed(1));

        if (distance >= this.mapService.DISTANCE_TO_BOUNDS) {
            this.currentCoords = mapCenter;
            return true;
        }

        return false;
    }

    public clearMarkers(): void {
        if (this.markers) {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < this.markers.length; i++) {
                this.map.removeOverlay(this.markers[i]);
            }
            this.markerClusterer.clearMarkers();
        }
    }

    public setMarkers(
        locations: MapLocation[],
        shouldShowDisabledIcon = false
    ): void {
        // TODO ECOMDUTI-23592 [TechDebt] - Desacoplar tiendas/droppoints de mapas.
        this.markers = locations.map((location) => this.addMarker(location, shouldShowDisabledIcon));
        // this.refreshClickedMarker();

        this.updateMarkerCluster();
    }

    public calculateBoundsChanged(): { center: number; radius: number } {
        const bounds = this.map.getBounds();
        const center: any = bounds.getCenter();
        const ne: any = bounds.getNorthEast();
        const sw: any = bounds.getSouthWest();
        const radius = Math.max(50, Math.ceil(this.mapService.computeDistance(sw.lat, sw.lng, ne.lat, ne.lng) / 1.75));
        const data: any = { center, radius };

        return data;
    }

    public unsetSelectedMarker(): void {
        // eslint-disable-next-line deprecation/deprecation
        const found: google.maps.Marker = this.markers?.find((marker) => {
            const markerIcon: any = marker.getIcon();

            return markerIcon?.tag === 'used';
        });

        if (found) {
            const icon = this.getIconFromMarker(found);

            found.setIcon(icon);
        }
    }

    public toggleShopMarker(coords: MapCoords): void {
        const bCoords = new BMap.Point(coords.lng, coords.lat);

        const found = this.markers?.find((marker) => {
            return marker.getPosition().equals(bCoords);
        });

        if (found) {
            const icon = this.getIconFromMarker(found);

            found.setIcon(icon);
        }
    }

    public setSelectedStore(location: DroppointListsDetail | PhysicalStoreDetail, zoom?: number): void {
        const coords: MapCoords = {
            lat: location.latitude,
            lng: location.longitude,
        };

        if (zoom) coords.zoom = zoom;

        this.setCenter(coords);
        this.toggleShopMarker(coords);
    }

    public geocodeFromString(address: string): Observable<any> {
        const geocorder = new BMap.Geocoder();
        const city = address;
        return new Observable((observer) => {
            // Si address viene vacio es porque se quiere obtener los datos del defaultCity (centro o capital del país)
            // Igual que hace google pasandole una cadena vacia
            geocorder.getPoint(address || this.defaultCity, (point) => {
                // Ésta funcionalidad debe ser ejecutada en el Zone de Angular ya que ocurre fuera del scope de Angular
                this.zone.run(() => {
                    if (point) {
                        observer.next(point);
                        observer.complete();
                    } else {
                        observer.error();
                    }
                });
            }, city);
        });
    }

    private addMarker(location: MapLocation, shouldShowDisabledIcon = false): any {
        const marker = new BMap.Marker(this.createCoords(location.latitude, location.longitude), {
            icon: this.getBaseIcon(location, shouldShowDisabledIcon),
            enableClicking: true,
            title: location.name,
        });

        marker.addEventListener('click', () => {
            // Ésta funcionalidad debe ser ejecutada en el Zone de Angular ya que ocurre fuera del scope de Angular
            this.zone.run(() => {
                if (!location.disabled) {
                    this.setMapEvent({ kind: 'clickMarker', data: { marker, location } });
                }
            });
        });

        return marker;
    }

    private getBaseIcon(location: MapLocation, shouldShowDisabledIcon = false): any {
        const mode = determineModeIconUseCase(location, shouldShowDisabledIcon);
        const url: string = this.mapService.getBaseIconUrl(mode, location.provider);

        const icon = this.createIcon(url, 48, 48);

        icon.tag = mode;

        if (location.provider) {
            icon.kind = location.provider;
        }

        return icon;
    }

    // eslint-disable-next-line deprecation/deprecation
    private getIconFromMarker(marker: google.maps.Marker): any {
        const markerIcon: any = marker.getIcon();
        const mode: string = markerIcon.tag === 'used' ? 'unused' : 'used';

        const url: string = this.mapService.getMarkerIconUrl(mode, markerIcon);

        const icon = this.createIcon(url, 48, 48);

        icon.id = markerIcon.id;
        icon.tag = mode;

        if (markerIcon.kind) {
            icon.kind = markerIcon.kind;
        }
        return icon;
    }

    private createIcon(url: string, width: number, height: number): any {
        return new BMap.Icon(url, new BMap.Size(width, height));
    }

    private createCoords(lat: number, lng: number): any {
        return new BMap.Point(lng, lat);
    }

    private updateMarkerCluster(): void {
        this.markerClusterer = new BMapLib.MarkerClusterer(this.map, {
            // maxZoom: 12,
            // gridSize: 100,
            markers: this.markers,
            styles: [
                {
                    url: this.mapService.getClusterInlineSvg('black'),
                    size: new BMap.Size(48, 48),
                    textColor: '#fff',
                    opt_textSize: 14,
                },
                {
                    url: this.mapService.getClusterInlineSvg('black'),
                    size: new BMap.Size(58, 58),
                    textColor: '#fff',
                    opt_textSize: 14,
                },
                {
                    url: this.mapService.getClusterInlineSvg('black'),
                    size: new BMap.Size(68, 68),
                    textColor: '#fff',
                    opt_textSize: 14,
                },
                {
                    url: this.mapService.getClusterInlineSvg('black'),
                    size: new BMap.Size(78, 78),
                    textColor: '#fff',
                    opt_textSize: 14,
                },
                {
                    url: this.mapService.getClusterInlineSvg('black'),
                    size: new BMap.Size(88, 88),
                    textColor: '#fff',
                    opt_textSize: 14,
                },
            ],
        });
    }
}
